'use client'

import { Container } from '@cleartrip/ct-design-components'
import React, { cloneElement, CSSProperties, isValidElement, memo, useCallback, useMemo, useState } from 'react'

import { IExpandWidgetAnimationWrapperProps, IExpandWidgetItemProps } from './type'

const ExpandWidgetAnimationWrapper: React.FC<IExpandWidgetAnimationWrapperProps> = ({ children, WidgetComponent }) => {
    const [shouldMoveChildren, setShouldMoveChildren] = useState(false)
    const [dynamicHeight, setDynamicHeight] = useState<number | null>(null)

    const totalHeight = useMemo(() => {
        if (isValidElement(WidgetComponent)) {
            return (WidgetComponent?.props as IExpandWidgetItemProps)?.widgetHeight ?? 0
        }
        return 0
    }, [WidgetComponent])

    const handleDataReady = useCallback(
        (height?: number) => {
            setDynamicHeight(height ?? totalHeight)
            setShouldMoveChildren(true)
        },
        [totalHeight]
    )

    const handleDataRemoved = useCallback(() => {
        setDynamicHeight(null)
        setShouldMoveChildren(false)
    }, [])

    const widgetWithHandlers = useMemo(() => {
        if (isValidElement(WidgetComponent)) {
            return cloneElement(WidgetComponent as React.ReactElement<IExpandWidgetItemProps>, {
                onDataReady: handleDataReady,
                onDataRemoved: handleDataRemoved
            })
        }
        return WidgetComponent
    }, [WidgetComponent, handleDataReady, handleDataRemoved])

    const translateY = useMemo(
        () => (shouldMoveChildren ? 0 : -(dynamicHeight ?? totalHeight)),
        [shouldMoveChildren, totalHeight, dynamicHeight]
    )

    const { widgetTransition, childrenTransition } = useMemo(() => {
        if (!shouldMoveChildren) {
            return { widgetTransition: 'none', childrenTransition: 'none' }
        }
        return {
            widgetTransition: 'transform 1s cubic-bezier(0.16, 1, 0.3, 1)',
            childrenTransition: 'transform 1s cubic-bezier(0.16, 1, 0.3, 1) 0.15s'
        }
    }, [shouldMoveChildren])

    const wrapperStyle: CSSProperties = useMemo(
        () => ({
            maxHeight: shouldMoveChildren ? dynamicHeight ?? totalHeight : 0,
            transition: shouldMoveChildren ? `max-height 1.2s cubic-bezier(0.16, 1, 0.3, 1)` : 'none',
            willChange: 'max-height'
        }),
        [shouldMoveChildren, totalHeight, dynamicHeight]
    )

    const widgetInnerStyle: CSSProperties = useMemo(
        () => ({
            transform: `translateY(${translateY}px)`,
            transition: widgetTransition,
            willChange: 'transform',
            backfaceVisibility: 'hidden'
        }),
        [translateY, widgetTransition]
    )

    const childrenStyle: CSSProperties = useMemo(
        () => ({
            zIndex: 2,
            transform: shouldMoveChildren ? `translateY(${translateY}px)` : 'none',
            transition: childrenTransition,
            willChange: 'transform',
            backfaceVisibility: 'hidden'
        }),
        [shouldMoveChildren, translateY, childrenTransition]
    )

    return (
        <Container className="p-relative">
            <Container className="p-relative o-hidden z-1" css={wrapperStyle}>
                <Container css={widgetInnerStyle}>{widgetWithHandlers}</Container>
            </Container>
            <Container className="p-relative" css={childrenStyle}>
                {children}
            </Container>
        </Container>
    )
}

export default memo(ExpandWidgetAnimationWrapper)
